import { useEffect } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';

import {
  clearCheckoutIntent,
  isPurchaseTracked,
  markPurchaseTracked,
  postFulfillCheckout,
  readCheckoutIntent,
  stripeAmountToValue,
  type CheckoutIntent,
  type FulfillCheckoutResponse,
} from '@/entities/payments';
import { courseQueries } from '@/entities/course';
import { usersQueries } from '@/entities/user/api/users.query';
import {
  toMetaContentId,
  trackPurchase,
  type MetaCheckoutParams,
} from '@/shared/analytics/meta-pixel';
import { useToast } from '@/shared/ui/toast-provider';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { Button } from '@/shared/ui/button';
import { useSession } from '@/app/providers/session-provider';
import { PageContainer } from '@/shared/ui/page-container';

const pageBackgroundClass = 'min-h-screen bg-slate-50 text-slate-700';

const buildPurchaseParams = (
  data: FulfillCheckoutResponse | undefined,
  intent: CheckoutIntent | null,
): MetaCheckoutParams | null => {
  // A legacy backend returns an empty body, which axios yields as ''
  const response = typeof data === 'object' && data !== null ? data : undefined;

  if (response?.amount_total != null && response.currency) {
    const courseId = response.course_id ?? intent?.course_id ?? '';
    return {
      value: stripeAmountToValue(response.amount_total, response.currency),
      currency: response.currency.toUpperCase(),
      content_ids: [toMetaContentId(courseId)],
      content_name: response.course_title ?? intent?.course_title ?? '',
      content_type: 'product',
      num_items: 1,
    };
  }

  if (intent) {
    return {
      value: intent.price,
      currency: intent.currency,
      content_ids: [toMetaContentId(intent.course_id)],
      content_name: intent.course_title,
      content_type: 'product',
      num_items: 1,
    };
  }

  return null;
};

const trackPurchaseOnce = (
  data: FulfillCheckoutResponse | undefined,
  sessionId: string,
) => {
  if (isPurchaseTracked(sessionId)) return;

  const params = buildPurchaseParams(data, readCheckoutIntent());
  if (params) {
    // session_id doubles as the Meta eventID so reloads dedup server-side too
    trackPurchase(params, sessionId);
    markPurchaseTracked(sessionId);
  } else {
    console.warn('[meta-pixel] Purchase not tracked: no real value available');
  }
  clearCheckoutIntent();
};

export const PaymentSuccessPage = () => {
  const router = useRouter();
  const { userRefetch } = useSession();
  const { session_id } = useSearch({
    from: '/_user/payment/success',
  });
  const toast = useToast();
  const queryClient = useQueryClient();

  const fulfillCheckoutMutation = useMutation({
    mutationFn: (payload: { session_id: string }) =>
      postFulfillCheckout(payload),
    onSuccess: async (data, variables) => {
      trackPurchaseOnce(data, variables.session_id);
      await userRefetch();
    },
    onError: (error) => {
      toast({
        type: 'error',
        title: 'We could not confirm your payment.',
        description: resolveErrorMessage(
          error,
          'Please try again shortly or contact support.',
        ),
      });
    },
  });

  const { mutate: fulfillCheckout } = fulfillCheckoutMutation;

  useEffect(() => {
    if (!session_id) return;
    fulfillCheckout({ session_id });
  }, [session_id, fulfillCheckout]);

  const handleContinue = () => {
    router.navigate({ to: '/courses' });
  };

  return (
    <main className={pageBackgroundClass}>
      <PageContainer maxWidth="mobile">
        <div className="flex py-12">
          <section className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-12 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-500 shadow-sm">
            {fulfillCheckoutMutation.isPending ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : (
              <CheckCircle2 className="h-8 w-8" />
            )}
          </div>

          <h1 className="mt-8 text-2xl font-semibold text-slate-900 sm:text-3xl">
            Payment confirmed!
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500 sm:text-base">
            Thank you for completing your purchase. Your course materials are
            ready whenever you are—continue to your dashboard to start learning.
          </p>

          <Button
            onClick={handleContinue}
            variant="default"
            size="lg"
            className="mt-10 inline-flex min-w-[12rem] items-center justify-center gap-2"
            disabled={fulfillCheckoutMutation.isPending}
          >
            {fulfillCheckoutMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Finalizing...
              </>
            ) : (
              'Go to my courses'
            )}
          </Button>

          {!session_id ? (
            <p className="mt-8 text-sm text-slate-400">
              Missing session details. If you believe this is an error, please
              contact support with your payment information.
            </p>
          ) : null}
        </section>
        </div>
      </PageContainer>
    </main>
  );
};
